import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SearchXIcon } from 'lucide-react';
import { ease } from '../../core/format/motion';
import type { IconComponent } from '../../ui/icon';
type Props = {
  title: string;
  text: ReactNode;
  /** Het adres dat niet bestaat, zodat Sarah ziet wát er mis is (bijv. een oude bladwijzer). */
  path?: string;
  icon?: IconComponent;
  /** Eerste actie is de hoofdactie; de rest staat ernaast als rustige link. */
  actions: ReactNode;
};

/**
 * "Niet gevonden" in het beheer: altijd zeggen wat er aan de hand is en een veilige weg terug
 * bieden, in plaats van stil naar het overzicht te springen. Eén zachte entree (opacity en 8px),
 * geen beweging bij "minder beweging".
 */
export function NotFoundView({
  title,
  text,
  path,
  icon: Icon = SearchXIcon,
  actions
}: Props) {
  const reduce = useReducedMotion();
  return <motion.section initial={reduce ? false : {
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.24,
    ease
  }} aria-labelledby="not-found-title" className="mx-auto flex max-w-lg flex-col items-center px-4 py-16 text-center sm:py-24" data-id="element-5071" data-mp="1yvwydkdbbvst">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-clay ring-1 ring-line" aria-hidden="true" data-id="element-5072" data-mp="2dez4qsjkq0oo">
        <Icon className="h-5 w-5" strokeWidth={1.5} data-id="element-5073" data-mp="2hh3xlp276ta9" />
      </span>
      <h1 id="not-found-title" className="mt-5 font-display text-3xl font-light leading-tight text-ink [text-wrap:balance]" data-id="element-5074" data-mp="35651cz035g97">
        {title}
      </h1>
      <div className="mt-3 max-w-md text-[14px] leading-relaxed text-muted" data-id="element-5075" data-mp="3a46te9fhopwt">{text}</div>
      {path && <p className="mt-4 max-w-full truncate rounded-full bg-white/70 px-3 py-1 font-mono text-[12px] text-muted ring-1 ring-line" data-id="element-5076" data-mp="2w4x9ayo5xbnk">
          <span className="sr-only" data-id="element-5077" data-mp="1kqbd8f88ef2o">Adres: </span>
          {path}
        </p>}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3" data-id="element-5078" data-mp="1pp4tugitem72">{actions}</div>
    </motion.section>;
}